import type { PreferredSpace } from '~/packages/general/variable/recommendedLayerForVariable.util'
import type { SpaceType } from '~/packages/general/variable/spaceTime.types'
import h3ResolutionResolve from '~/prix/utils/h3ResolutionResolve'

type PreferredGeneralSettingsLike = {
  preferredSpaceDelimitationType?: string | null
  preferredSpaceDelimitationId?: string | number | null
  preferredSpaceDelimitationTitle?: string | null
  preferredSpaceChildrenType?: string | null
} | null

type DelimitationLike = {
  delimitationType?: string | null
  delimitationId?: string | number | null
} | null

type StatsSpaceLike = DelimitationLike & {
  childrenType?: string | null
  childrenItemView?: string | null
  childrenH3Resolution?: number | null
  [key: string]: unknown
}

function normalizeDelimitationType(type: string | null | undefined) {
  return type === 'city' ? 'municipality' : type ?? null
}

export function getPreferredSpaceFromGeneralSettings(
  settings: PreferredGeneralSettingsLike | undefined,
): PreferredSpace | null {
  if (
    !settings?.preferredSpaceDelimitationType ||
    settings.preferredSpaceDelimitationId == null ||
    !settings.preferredSpaceDelimitationTitle
  ) {
    return null
  }

  return {
    delimitationType: normalizeDelimitationType(
      settings.preferredSpaceDelimitationType,
    ) as PreferredSpace['delimitationType'],
    delimitationId: Number(settings.preferredSpaceDelimitationId),
    delimitationTitle: settings.preferredSpaceDelimitationTitle,
    childrenType: normalizeDelimitationType(
      settings.preferredSpaceChildrenType,
    ) as PreferredSpace['childrenType'],
  }
}

export function isSameAsPreferredDelimitation(
  space: DelimitationLike,
  preferredSpace: Pick<PreferredSpace, 'delimitationType' | 'delimitationId'> | null | undefined,
) {
  if (!space || !preferredSpace) {
    return false
  }

  const spaceDelimitationType = normalizeDelimitationType(space.delimitationType)
  const preferredDelimitationType = normalizeDelimitationType(preferredSpace.delimitationType)

  if (!spaceDelimitationType || !preferredDelimitationType) {
    return false
  }

  return (
    spaceDelimitationType === preferredDelimitationType &&
    String(space.delimitationId) === String(preferredSpace.delimitationId)
  )
}

export function buildStatsSpaceForUserScope(
  space: StatsSpaceLike | null | undefined,
  preferredSpace: Pick<PreferredSpace, 'delimitationType' | 'delimitationId'> | null | undefined,
) {
  if (!space) {
    return undefined
  }

  if (!isSameAsPreferredDelimitation(space, preferredSpace)) {
    return space
  }

  const { delimitationType, delimitationId, ...rest } = space
  if (
    rest.childrenType === 'item' &&
    (rest.childrenItemView === 'cluster' || rest.childrenItemView === 'heatmap') &&
    rest.childrenH3Resolution == null
  ) {
    return {
      ...rest,
      childrenH3Resolution: h3ResolutionResolve(
        normalizeDelimitationType(space.delimitationType) as SpaceType | null | undefined,
      ),
    }
  }

  return rest
}
